import { Link, useParams } from "react-router-dom";
import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { ArrowLeft } from "lucide-react";

import { api, type ControllerMeter, type ControllerTelemetryPoint } from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { KpiCard } from "@/components/kpi-card";
import { PageHeader } from "@/components/page-header";
import { TelemetryChartGrid } from "@/components/charts/telemetry-chart-grid";
import {
  formatCurrent,
  formatControllerName,
  formatDeviceId,
  formatEnergyKwh,
  formatFrequency,
  formatPowerFactor,
  formatPowerKw,
  formatTimestamp,
  formatVoltage,
} from "@/lib/format";

export function MeterDetailPage(): JSX.Element {
  const { deviceId = "", meterId = "" } = useParams<{ deviceId: string; meterId: string }>();

  const controllerQuery = useQuery({
    queryKey: ["controller", deviceId],
    queryFn: () => api.controller(deviceId),
    enabled: Boolean(deviceId),
  });
  const telemetryQuery = useQuery({
    queryKey: ["controller-telemetry", deviceId],
    queryFn: () => api.controllerTelemetry(deviceId),
    enabled: Boolean(deviceId),
  });

  const controller = controllerQuery.data;
  const meter = useMemo(
    () => controller?.meters.find((candidate) => String(candidate.modbus_id) === meterId) ?? null,
    [controller?.meters, meterId],
  );
  const points = useMemo(
    () => (telemetryQuery.data ?? []).filter((point) => point.meter_id === meterId),
    [telemetryQuery.data, meterId],
  );
  const latest = points[0] ?? null;

  if (controllerQuery.error) {
    return (
      <>
        <PageHeader title="Meter" />
        <div className="rounded-md border border-destructive/40 bg-destructive/10 px-4 py-3 text-sm text-destructive">
          {controllerQuery.error instanceof Error
            ? controllerQuery.error.message
            : "Failed to load meter"}
        </div>
      </>
    );
  }

  if (controllerQuery.isLoading || !controller) {
    return (
      <>
        <PageHeader title="Loading meter..." />
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {Array.from({ length: 6 }).map((_, idx) => (
            <Skeleton key={idx} className="h-28" />
          ))}
        </div>
      </>
    );
  }

  return (
    <>
      <PageHeader
        title={meterLabel(meter, meterId)}
        description={`${formatControllerName(controller.display_name, controller.serial_alias)} · ${formatDeviceId(controller.device_id)} · Modbus ${meterId}`}
        actions={
          <Button asChild variant="outline" size="sm">
            <Link to={`/controllers/${encodeURIComponent(controller.device_id)}`}>
              <ArrowLeft className="h-4 w-4" /> Back to controller
            </Link>
          </Button>
        }
      />

      <div className="mb-6 flex flex-wrap items-center gap-2">
        <Badge variant={meter?.enabled === false ? "muted" : "success"}>
          {meter?.enabled === false ? "disabled" : "enabled"}
        </Badge>
        <Badge variant={meter?.source_mode === "feeder" ? "secondary" : "outline"}>
          Source: {meter?.source_mode ?? "telemetry discovered"}
        </Badge>
        {meter?.feeder_id !== null && meter?.feeder_id !== undefined ? (
          <Badge variant="muted">
            Feeder {meter.feeder_id} rev {meter.feeder_revision ?? "--"}
          </Badge>
        ) : null}
        <span className="text-xs text-muted-foreground">
          Last telemetry {formatTimestamp(latest?.ingested_at ?? null)}
        </span>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <KpiCard label="Active Power" value={formatPowerKw(latest?.active_power_inst_x10 ?? null)} />
        <KpiCard label="Voltage L1-L2" value={formatVoltage(latest?.v_l12_x100 ?? null)} />
        <KpiCard label="Current L1" value={formatCurrent(latest?.a_l1_x100 ?? null)} />
        <KpiCard label="Power Factor" value={formatPowerFactor(latest?.power_factor_x1000 ?? null)} />
        <KpiCard label="Frequency" value={formatFrequency(latest?.frequency_x100 ?? null)} />
        <KpiCard label="Energy" value={formatEnergyKwh(latest?.active_energy_tot_wh ?? null)} />
        <KpiCard label="Samples Loaded" value={String(points.length)} />
        <KpiCard label="Configured Slot" value={meter ? String(meter.slot) : "--"} />
      </div>

      <div className="mt-6 grid gap-6 lg:grid-cols-[minmax(0,1fr)_360px]">
        <TelemetryChartGrid loading={telemetryQuery.isLoading} points={points} />
        <MeterConfigCard meter={meter} telemetryPoints={points} />
      </div>
    </>
  );
}

function MeterConfigCard({
  meter,
  telemetryPoints,
}: {
  meter: ControllerMeter | null;
  telemetryPoints: ControllerTelemetryPoint[];
}): JSX.Element {
  return (
    <Card className="h-fit">
      <CardHeader>
        <CardTitle>Meter assignment</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3 text-sm">
        {meter ? (
          <>
            <ConfigRow label="Slot" value={String(meter.slot)} />
            <ConfigRow label="Modbus ID" value={String(meter.modbus_id)} />
            <ConfigRow label="Label" value={meter.label || "--"} />
            <ConfigRow label="Source" value={meter.source_mode} />
            <ConfigRow label="Feeder ID" value={meter.feeder_id === null ? "--" : String(meter.feeder_id)} />
            <ConfigRow label="CT" value={`${meterCtPrimary(meter)}:${meterCtSecondary(meter)}`} />
            <ConfigRow label="VT" value={`${meterVtPrimary(meter)}:${meterVtSecondary(meter)}`} />
          </>
        ) : (
          <p className="text-muted-foreground">
            This meter ID has telemetry but no saved controller meter assignment yet.
          </p>
        )}
        <div className="rounded-md border border-border bg-muted/30 p-3 text-xs text-muted-foreground">
          The feeder/custom assignment is what compiles into `berry.script_config.m`; telemetry
          validates that the Modbus meter stream exists for this controller.
        </div>
        {telemetryPoints.length === 0 ? (
          <div className="rounded-md border border-amber-500/30 bg-amber-500/10 p-3 text-xs text-amber-200">
            No telemetry samples were returned for this meter ID in the current window.
          </div>
        ) : null}
      </CardContent>
    </Card>
  );
}

function ConfigRow({ label, value }: { label: string; value: string }): JSX.Element {
  return (
    <div className="flex items-center justify-between gap-4 border-b border-border/60 pb-2 last:border-0">
      <span className="text-muted-foreground">{label}</span>
      <span className="font-mono text-xs text-foreground">{value}</span>
    </div>
  );
}

function meterLabel(meter: ControllerMeter | null, meterId: string): string {
  return meter?.label ? `${meter.label} (${meterId})` : `Meter ${meterId}`;
}

function meterCtPrimary(meter: ControllerMeter): string {
  return String(meter.resolved_ct_primary ?? meter.custom_ct_primary ?? "--");
}

function meterCtSecondary(meter: ControllerMeter): string {
  return String(meter.resolved_ct_secondary ?? meter.custom_ct_secondary ?? "--");
}

function meterVtPrimary(meter: ControllerMeter): string {
  return String(meter.resolved_vt_primary ?? meter.custom_vt_primary ?? "--");
}

function meterVtSecondary(meter: ControllerMeter): string {
  return String(meter.resolved_vt_secondary ?? meter.custom_vt_secondary ?? "--");
}
